import { CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment/moment';
import { useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../services/api-client/contact-api';
import { TreatmentPlanModel } from '../../../../../../services/api-client/contact-api.types';
import { AutoFollowUpStatusComponent } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/auto-followup-status/auto-followup-status';
import { TreatmentPlanItemsDialog } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/edit-items-dialog/treatment-plan-items-dialog';
import { TreatmentPlanStatusComponent } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/treatment-plan-status';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { Card, CardTitle, Table } from '../../patient-profile-page.styles';

import TreatmentPlanTableSkeleton from './treatment-plans-skeleton';
import { ClickableDescription, Container } from './treatment-plans.styles';

export const TreatmentPlansView = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { contactDetails, locationId } = usePatientProfile();
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TreatmentPlanModel>();
  const [dialogOpened, setDialogOpened] = useState(false);
  const { showError } = useNotification();
  const contactApi = useMemo(() => ContactApi.initialize(config, oidcService), []);

  useEffect(() => {
    loadTreatmentPlans();
  }, []);

  const loadTreatmentPlans = async () => {
    setIsLoading(true);

    try {
      const treatmentPlans = await contactApi.getTreatmentPlans(contactDetails.id, locationId);

      setTreatmentPlans(treatmentPlans);
    } catch {
      showError('Unable to load treatment plans.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (plan: TreatmentPlanModel) => {
    setSelectedItem(plan);
    setDialogOpened(true);
  };

  const handleNoteDialogClose = async () => {
    setDialogOpened(false);
    setSelectedItem(undefined);
  };

  const handleTreatmentPlanUpdate = (plan: TreatmentPlanModel) => {
    setTreatmentPlans((prev) => prev.map((tp) => (tp.id === plan.id ? plan : tp)));
    setSelectedItem(plan);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <CardTitle>
            {contactDetails?.firstName} {contactDetails?.lastName}’s Treatment Plans
          </CardTitle>
          {isLoading && <TreatmentPlanTableSkeleton />}
          {!isLoading && treatmentPlans.length === 0 && <NoDataComponent />}
          {!isLoading && treatmentPlans.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '15%' }}>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell style={{ width: '20%' }}>Followup</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treatmentPlans.map((tp) => (
                  <TableRow key={tp.id} onClick={() => handleItemClick(tp)}>
                    <TableCell style={{ width: '15%' }}>
                      {tp.dateCreated ? moment(tp.dateCreated).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>
                      <ClickableDescription onClick={() => handleItemClick(tp)}>
                        {tp.description || '-'}
                      </ClickableDescription>
                    </TableCell>
                    <TableCell>
                      <TreatmentPlanStatusComponent status={tp.status} sx={{ fontWeight: 400 }} />
                    </TableCell>
                    <TableCell style={{ width: '20%' }}>
                      <AutoFollowUpStatusComponent autoFollowUpStatus={tp.autoFollowUpStatus} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {selectedItem && (
        <TreatmentPlanItemsDialog
          isOpen={dialogOpened}
          treatmentPlan={selectedItem}
          onTreatmentPlanUpdate={handleTreatmentPlanUpdate}
          onClose={handleNoteDialogClose}
        />
      )}
    </Container>
  );
};
